/**
 * Bidirectional Text Detection for Layout Items
 *
 * Utilities for detecting Arabic and Latin text in layout items.
 * Used to determine text direction for rendering.
 */

import type { LayoutItem } from '../../transformers/zod-schemas';
import { JSON_TYPES } from '../../constants';
import { isArabic, hasLatinChars } from './text-direction';

/**
 * Check if a layout item contains Arabic text
 *
 * Recursively checks text-based layout items and groups.
 *
 * @param item - The layout item to check
 * @returns true if item contains any Arabic text
 */
export function itemContainsArabic(item: LayoutItem): boolean {
  if (item.type === JSON_TYPES.TEXT && 'value' in item) {
    return isArabic(item.value);
  }
  if (item.type === JSON_TYPES.HIGHLIGHTED_TEXT && 'value' in item) {
    return isArabic(item.value);
  }
  if (item.type === JSON_TYPES.GROUP && 'items' in item) {
    return item.items.some((subItem: LayoutItem) => itemContainsArabic(subItem));
  }
  return false;
}

/**
 * Check if a layout item contains Latin text
 *
 * Recursively checks text-based layout items and groups.
 *
 * @param item - The layout item to check
 * @returns true if item contains any Latin characters
 */
export function itemContainsLatin(item: LayoutItem): boolean {
  if (item.type === JSON_TYPES.TEXT && 'value' in item) {
    return hasLatinChars(item.value);
  }
  if (item.type === JSON_TYPES.HIGHLIGHTED_TEXT && 'value' in item) {
    return hasLatinChars(item.value);
  }
  if (item.type === JSON_TYPES.GROUP && 'items' in item) {
    return item.items.some((subItem: LayoutItem) => itemContainsLatin(subItem));
  }
  return false;
}

/**
 * Check if layout items contain mixed Arabic and Latin text
 *
 * @param items - Array of layout items
 * @returns true if items contain both Arabic and Latin characters
 */
export function hasMixedLanguages(items: LayoutItem[]): boolean {
  const hasArabic = items.some(itemContainsArabic);
  const hasLatin = items.some(itemContainsLatin);
  return hasArabic && hasLatin;
}

/**
 * Determine if items should use RTL direction
 *
 * @param items - Array of layout items
 * @returns true if items are pure Arabic (no Latin)
 */
export function shouldItemsUseRTL(items: LayoutItem[]): boolean {
  const hasArabic = items.some(itemContainsArabic);
  const hasLatin = items.some(itemContainsLatin);
  return hasArabic && !hasLatin;
}
